/**
 * @typedef {Object} CrossSellingListItem
 * @property {string} imageUrl - URL of the variant image
 * @property {string} productImageUrl - URL of the product image
 * @property {string} productTitle - Title of the product
 * @property {string} productHandle - handle of the product
 * @property {string} variantTitle - Title of the variant
 * @property {string} quantity - Quantity of the variant
 * @property {string} variantId - Shopify variant GID
 */

class CrossSellingCartComponent extends HTMLElement {
  constructor() {
    super();

    /** @type {CrossSellingListItem[] | []} */
    this.crossSellingVariantList =
      /** @type {CrossSellingListItem[]} */ window.crossSellingVariantsJson ||
      [];

    if (
      !this.crossSellingVariantList ||
      this.crossSellingVariantList.length === 0
    ) {
      console.error("Cannot get variant list for cross selling");
      return;
    }
  }

  async connectedCallback() {
    this.templateName = this.dataset.templateName || "unknown";
    /**
     * @type {HTMLElement | null}
     */
    this.wrapperElement = this;
    this.imageElement = this.querySelector("img");
    this.productTitleElement = this.querySelector(".product-title");
    this.productPriceElement = this.querySelector(".ajaxcart__price");
    /**
     * @type {HTMLElement | null}
     */
    this.variantTitleElement = this.querySelector(".variant-title");
    this.compareAtPriceElement = this.querySelector("s");
    this.addToCartButton = this.querySelector(".button");
    this.closeX = this.querySelector(".cross-selling__close-button");
    this.holidayShippingEnabled = this.dataset.holidayShipping === "true";
    this.mutationObserver = null;

    if (this.closeX) {
      this.closeX.addEventListener("click", async (event) => {
        event.preventDefault();

        if (!this.fullVariant) {
          console.warn("No variant selected to delete");
          this.hideWrapper();
          return;
        }

        store.addToDeletedVariantsList(this.fullVariant.id);
        this.hideWrapper();
        await this.renderVariant(false);

        // Recalculate cartFooterHeight and update cartInner bottom style
        const cartFooter = document.getElementById("cartDrawerFooter");
        const cartInner = document.getElementById("cartDrawerInner");
        if (cartFooter && cartInner) {
          let cartFooterHeight = cartFooter.offsetHeight;
          cartInner.style.bottom = `${cartFooterHeight}px`;
        }
      });
    }

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener("click", async (event) => {
        event.preventDefault();

        if (
          !this.firstValidCrossSellingVariant ||
          !this.fullProduct ||
          !this.fullVariant
        ) {
          console.error("Cannot add to cart");
          return;
        }

        const promoCode = this.getPromoCode();

        const itemObj = {
          id: this.fullVariant.id,
          quantity: 1,
          properties: {
            ...(promoCode.trim() ? { "_promo-code": promoCode } : {}),
            ...(this.holidayShippingEnabled
              ? {}
              : { "_ships-by": this.shipsBy || "" }),
          },
        };

        const items = [itemObj];

        const payload = JSON.stringify({ items });

        try {
          await store.addToCart(payload);

          if (!this.wrapperElement) {
            return;
          }
          this.hideWrapper();
        } catch (error) {
          console.error("Failed to add cross-sell item to cart:", error);
        }

        // send analytics
        if (!Shopify.analytics) {
          console.log("Shopify.analytics is not available");
          return;
        }
        try {
          const payloadAnalytics = {
            source: `cross-selling-cart_${this.templateName}-page`,
            productId: this.fullProduct.id,
            variantId: this.fullVariant.id,
          };

          console.log("publishing payload: ", payloadAnalytics);
          Shopify.analytics.publish("kn_add_to_cart", payloadAnalytics);
        } catch (error) {
          console.error("Failed to publish analytics payload:", error);
        }
      });
    }

    messenger.listen(
      messenger.topics.CART_READY,
      async () => {
        this.firstValidCrossSellingVariant =
          await this.getValidCrossSellingVariant();
        if (!this.firstValidCrossSellingVariant) {
          this.hideWrapper();
          return;
        } else {
          this.renderVariant();
        }
      },
      "CrossSellingComponent"
    );

    const cartDrawerInner = document.querySelector(
      ".cart-drawer__inner-content"
    );
    if (cartDrawerInner) {
      this.mutationObserver = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            if (
              this.wrapperElement &&
              this.wrapperElement.classList.contains(
                "cross-selling-wrapper__show"
              )
            ) {
              setTimeout(() => {
                cartDrawerInner.classList.add("has-cross-selling");
              }, 500); // Delay addition by 0.5 seconds
            } else {
              cartDrawerInner.classList.remove("has-cross-selling");
            }
          }
        }
      });

      this.mutationObserver.observe(this.wrapperElement, { attributes: true });
    }
  }

  disconnectedCallback() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  getPromoCode() {
    return knocking.getCookie("promo_code");
  }

  showWrapper() {
    if (!this.wrapperElement) {
      return;
    }
    this.wrapperElement.classList.remove("cross-selling-wrapper__hide");
    this.wrapperElement.classList.add("cross-selling-wrapper__show");
    this.wrapperElement.inert = false;
    this.wrapperElement.removeAttribute("aria-hidden");

    const cartDrawerInner = document.querySelector(
      ".cart-drawer__inner-content"
    );
    if (cartDrawerInner) {
      cartDrawerInner.classList.add("has-cross-selling");
    }
  }

  hideWrapper() {
    if (!this.wrapperElement) {
      return;
    }
    this.wrapperElement.classList.remove("cross-selling-wrapper__show");
    this.wrapperElement.classList.add("cross-selling-wrapper__hide");
    this.wrapperElement.inert = true;
    this.wrapperElement.setAttribute("aria-hidden", "true");

    const cartDrawerInner = document.querySelector(
      ".cart-drawer__inner-content"
    );
    if (cartDrawerInner) {
      cartDrawerInner.classList.remove("has-cross-selling");
    }
  }
  /**
   * @returns {Promise<void>}
   * */
  async renderVariant(showWrapper = true) {
    if (!this.firstValidCrossSellingVariant) {
      console.error("Cannot get first valid cross selling variant");
      return;
    }
    if (!this.firstValidCrossSellingVariant.productTitle) {
      console.error("Cannot get product title");
      return;
    }

    const imageToDisplay = this.firstValidCrossSellingVariant.imageUrl;

    const productHandle =
      this.firstValidCrossSellingVariant.productHandle ||
      knocking.handleize(this.firstValidCrossSellingVariant.product);
    this.fullProduct = await knocking.getProductByHandle(productHandle);

    if (!this.fullProduct) {
      console.error("Cannot get full product");
      this.hideWrapper();
      return;
    }

    const tags = this.fullProduct.tags;

    this.fullVariant = this.fullProduct.variants.find(
      /** @param {Variant} variantItem */ (variantItem) =>
        variantItem.id ===
        parseInt(
          this.firstValidCrossSellingVariant.variantId.replace(
            "gid://shopify/ProductVariant/",
            ""
          ) || "0"
        )
    );

    if (!this.fullVariant) {
      console.error("Cannot get variant");
      return;
    }
    this.shipsBy = knocking.getTagShippingDate(tags);

    const linkToPPUrl = `/products/${this.fullProduct.handle}?variant=${this.fullVariant.id}`;
    this.setATags(linkToPPUrl);

    if (this.imageElement) {
      let imageSource;
      if (imageToDisplay) {
        imageSource = imageToDisplay;
      } else if (this.fullVariant.featured_image) {
        imageSource = this.fullVariant.featured_image.src;
      } else {
        imageSource = this.fullProduct.featured_image;
      }

      if (imageSource) {
        this.imageElement.src = imageSource;
      }
    }

    if (this.productTitleElement) {
      this.productTitleElement.textContent = this.fullProduct.title;
    }
    if (this.variantTitleElement) {
      if (this.fullVariant.title === "Default Title") {
        this.variantTitleElement.style.display = "none";
      } else {
        this.variantTitleElement.textContent = this.fullVariant.title;
        this.variantTitleElement.style.display = "inline-block";
      }
    }
    if (this.productPriceElement) {
      this.productPriceElement.textContent =
        knocking.formatToDollarCurrencyString(this.fullVariant.price);
    }
    if (this.compareAtPriceElement && this.fullVariant.compare_at_price) {
      this.compareAtPriceElement.textContent =
        knocking.formatToDollarCurrencyString(
          this.fullVariant.compare_at_price
        );
      this.compareAtPriceElement.removeAttribute("aria-hidden");
    } else if (this.compareAtPriceElement) {
      this.compareAtPriceElement.setAttribute("aria-hidden", "true");
    }

    if (!this.wrapperElement) {
      return;
    }
    if (showWrapper) {
      this.showWrapper();
    }
  }

  async getValidCrossSellingVariant() {
    this.currentCart = await store.getCart();

    if (!this.currentCart) {
      console.error("Cannot get current cart");
      return;
    }

    this.variantIdsInCart =
      this.currentCart.items.map(
        /** @param {Item} item */ (item) => item.variant_id
      ) || [];

    let validVariant = null;

    for (let i = 0; i < this.crossSellingVariantList.length; i++) {
      let crossSellingVariant = this.crossSellingVariantList[i];
      // if (parseInt(crossSellingVariant.quantity) <= 0) {
      //   continue;
      // }
      let variantFound = null;

      if (this.variantIdsInCart && this.variantIdsInCart.length > 0) {
        const variantId = crossSellingVariant.variantId
          ? parseInt(
              crossSellingVariant.variantId.replace(
                "gid://shopify/ProductVariant/",
                ""
              )
            )
          : crossSellingVariant.variant;

        const variantIsOnDeletedList =
          store.checkDeletedVariantsListForVariant(variantId);

        const variantAlreadyInCart = this.variantIdsInCart.includes(variantId);

        if (!variantAlreadyInCart && !variantIsOnDeletedList) {
          variantFound = crossSellingVariant;
        } else {
          continue;
        }
      } else {
        variantFound = crossSellingVariant;
      }

      if (!variantFound) {
        continue;
      }

      const productHandle =
        variantFound.productHandle || knocking.handleize(variantFound.product);

      let fullProduct;

      try {
        fullProduct = await knocking.getProductByHandle(productHandle);
      } catch (error) {
        console.error("Error fetching product:", error);
      }

      if (!fullProduct) {
        console.warn("Cannot get full product, skipping...");
        continue;
      }

      let isCustomizableProduct = false;
      for (let tag of fullProduct.tags) {
        if (tag.includes("customtext") || tag.includes("custom-sku")) {
          isCustomizableProduct = true;
          break;
        }
      }

      if (isCustomizableProduct) {
        continue;
      }

      let variantId;
      if (variantFound.variantId) {
        variantId = parseInt(
          variantFound.variantId.replace("gid://shopify/ProductVariant/", "")
        );
      } else if (variantFound.variant) {
        variantId = variantFound.variant;
      } else {
        return;
      }

      const fullVariant = fullProduct.variants.find(
        /** @param {Variant} variantItem */ (variantItem) =>
          variantItem.id === variantId
      );

      if (fullVariant && fullVariant.available) {
        validVariant = variantFound;
        return validVariant;
      } else {
        continue;
      }
    }

    return null;
  }

  /**
   * @param {string} url
   */
  setATags(url) {
    const aTags = this.querySelectorAll("a");
    aTags.forEach((tag) => {
      tag.href = url;
    });
  }
}

customElements.define(
  "cross-selling-cart-component",
  CrossSellingCartComponent
);
